import { inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { lastValueFrom, Observable } from 'rxjs';
import { SnackBarService } from '@core/services/snackbar.service';
import { Attachment } from '@shared/interfaces/attachment.interface';
import { Entry } from '@shared/interfaces/entry.interface';
import { AttachmentService } from '@shared/services/attachment.service';
import { AttachmentsManageService } from '@shared/modules/attachment-manage/services/attachments-manage.service';
import { ProjectAPIService } from '@modules/projects/shared/services/project-api.service';
import { ProjectCreatorApiService } from '@modules/project-creator/shared/services/project-creator-api.service';
import { EConfiguratorFileGroup } from '../models/configurator.model';
import {
  CONFIGURATOR_FILE_GROUPS,
  getEntryTypeOf,
  isAcceptedFile,
  isImageFile,
  isTooLarge,
  MAX_FILES_PER_ENTRY
} from '../config/configurator-files.config';
import { ConfiguratorStoreService } from './configurator-store.service';

const T = 'ProjectConfiguratorV2.Files';

/** A file on its way to the server, shown with a progress bar. */
export interface IConfiguratorUpload {
  id: string;
  group: EConfiguratorFileGroup;
  name: string;
  progress: number;
  /** Object URL of an image, for the thumbnail. */
  preview: string | null;
}

/** An uploaded file and the entry type it is stored in. */
export interface IConfiguratorFile {
  entryType: string;
  attachment: Attachment;
}

type UploadEvent = { progress: number; result?: unknown };

/**
 * Files of the configurator. Each group is stored as project entries (`data-box-2`, one per entry type) like the
 * documents of the old creator: the first files create an attachment packet and the entry with it, later files
 * are attached to the entry. Files are uploaded at once (PLAN, stage 6); the answers keep the entry ids
 * (`fileEntries`), "Habe ich nicht" (`filesSkipped`) and the counts per group (`fileCounts`) for the summary.
 */
@Injectable()
export class ConfiguratorFilesService {
  private readonly store = inject(ConfiguratorStoreService);
  private readonly projectApi = inject(ProjectAPIService);
  private readonly entryApi = inject(ProjectCreatorApiService);
  private readonly attachmentService = inject(AttachmentService);
  private readonly packetService = inject(AttachmentsManageService);
  private readonly translate = inject(TranslateService);
  private readonly snackBar = inject(SnackBarService);

  /** Uploaded files per entry type. */
  readonly files = signal<Record<string, Attachment[]>>({});
  readonly uploads = signal<IConfiguratorUpload[]>([]);
  readonly loaded = signal(false);
  /** Group last clicked or focused: a paste outside the paste zones goes there (else to the camera group). */
  readonly pasteTarget = signal<EConfiguratorFileGroup | null>(null);

  private projectId: number | null = null;
  /** Entries being created; files added meanwhile wait for them instead of creating a second entry. */
  private readonly pendingEntries = new Map<string, Promise<number | null>>();
  private uploadCounter = 0;

  /** Loads the files of the entries created so far (the answers must be restored first). */
  init(projectId: number): void {
    this.projectId = projectId;
    const entryIds = this.store.answers().fileEntries || {};
    if (!Object.keys(entryIds).length) {
      this.loaded.set(true);
      this.syncCounts();
      return;
    }

    this.projectApi.getProject(projectId).subscribe({
      next: (project) => {
        const entries: Entry[] = project?.dataBox2?.entries || [];
        const files: Record<string, Attachment[]> = {};
        Object.entries(entryIds).forEach(([entryType, entryId]) => {
          const entry = entries.find((item) => item.id === entryId && !item.deleted);
          // An entry removed in the project dashboard is created again with the next file.
          if (!entry) {
            this.store.setAnswer(`fileEntries.${entryType}`, null);
            return;
          }
          files[entryType] = (entry.attachments || [])
            .filter((attachment) => !attachment.deleted)
            .map((attachment) => new Attachment(attachment));
        });
        this.files.set(files);
        this.loaded.set(true);
        this.syncCounts();
      },
      error: () => this.loaded.set(true)
    });
  }

  /** "Neue Anfrage": the files stay in the project, the new request starts without them. */
  reset(): void {
    this.files.set({});
  }

  filesOf(group: EConfiguratorFileGroup): IConfiguratorFile[] {
    const files = this.files();
    return CONFIGURATOR_FILE_GROUPS[group].entryTypes.reduce<IConfiguratorFile[]>(
      (result, entryType) => [
        ...result,
        ...(files[entryType] || []).map((attachment) => ({ entryType, attachment }))
      ],
      []
    );
  }

  uploadsOf(group: EConfiguratorFileGroup): IConfiguratorUpload[] {
    return this.uploads().filter((upload) => upload.group === group);
  }

  isSkipped(group: EConfiguratorFileGroup): boolean {
    return !!this.store.answers().filesSkipped?.includes(group);
  }

  setSkipped(group: EConfiguratorFileGroup, skipped: boolean): void {
    const current = (this.store.answers().filesSkipped || []).filter((key) => key !== group);
    this.store.setAnswer('filesSkipped', skipped ? [...current, group] : current);
  }

  /** Uploads the accepted files; others are reported in a toast. Adding files undoes "Habe ich nicht". */
  add(group: EConfiguratorFileGroup, files: File[]): void {
    if (!this.projectId || !files.length) return;

    const accepted = files.filter((file) => isAcceptedFile(group, file));
    const fitting = accepted.filter((file) => !isTooLarge(file));
    if (accepted.length < files.length) this.snackBar.error(this.translate.instant(`${T}.notAccepted`));
    if (fitting.length < accepted.length) this.snackBar.error(this.translate.instant(`${T}.tooLarge`));
    if (!fitting.length) return;

    if (this.isSkipped(group)) this.setSkipped(group, false);

    const byEntryType = fitting.reduce<Record<string, File[]>>((result, file) => {
      const entryType = getEntryTypeOf(group, file);
      return { ...result, [entryType]: [...(result[entryType] || []), file] };
    }, {});

    Object.entries(byEntryType).forEach(([entryType, entryFiles]) => {
      const room = MAX_FILES_PER_ENTRY - (this.files()[entryType]?.length || 0);
      if (entryFiles.length > room) this.snackBar.error(this.translate.instant(`${T}.tooMany`));
      if (room > 0) this.uploadToEntry(group, entryType, entryFiles.slice(0, room));
    });
  }

  rename(file: IConfiguratorFile, name: string): void {
    const trimmed = name.trim();
    if (!trimmed || trimmed === file.attachment.name) return;

    this.attachmentService.renameAttachment(file.attachment.id, trimmed).subscribe({
      next: () =>
        this.updateFiles(file.entryType, (list) =>
          list.map((item) =>
            item.id === file.attachment.id ? Object.assign(new Attachment(item), { name: trimmed }) : item
          )
        ),
      error: () => this.snackBar.error(this.translate.instant(`${T}.renameError`))
    });
  }

  remove(file: IConfiguratorFile): void {
    this.attachmentService.removeAttachment(file.attachment.id).subscribe({
      next: () => {
        this.updateFiles(file.entryType, (list) => list.filter((item) => item.id !== file.attachment.id));
        this.snackBar.success(this.translate.instant(`${T}.removed`));
      },
      error: () => this.snackBar.error(this.translate.instant(`${T}.removeError`))
    });
  }

  private async uploadToEntry(
    group: EConfiguratorFileGroup,
    entryType: string,
    files: File[]
  ): Promise<void> {
    const uploads = files.map((file) => this.startUpload(group, file));
    const knownId = this.store.answers().fileEntries?.[entryType];
    const pending = this.pendingEntries.get(entryType);

    try {
      if (!knownId && !pending) {
        // Registered before the first await, so files added meanwhile wait for this entry.
        const created = this.createEntry(group, entryType, files, uploads);
        this.pendingEntries.set(entryType, created);
        await created.finally(() => this.pendingEntries.delete(entryType));
        return;
      }

      const entryId = knownId || (await pending);
      if (!entryId) throw new Error('No entry');
      // One request per file, so each one shows its own progress and a failed one does not stop the others.
      await Promise.all(
        files.map((file, index) => this.attachToEntry(entryId, entryType, file, uploads[index]))
      );
    } catch {
      this.snackBar.error(this.translate.instant(`${T}.uploadError`));
    } finally {
      uploads.forEach((upload) => this.finishUpload(upload));
    }
  }

  private async attachToEntry(entryId: number, entryType: string, file: File, upload: IConfiguratorUpload) {
    const result = await this.track(
      this.attachmentService.uploadAttachmentWithProgress({
        objectId: entryId,
        objectType: 'protocol-entry',
        files: [file]
      }),
      [upload]
    );
    const attachments = ((result as Attachment[]) || []).map((attachment) => new Attachment(attachment));
    this.updateFiles(entryType, (list) => [...list, ...attachments]);
  }

  /** The first files of an entry type: an attachment packet, then the entry with it. */
  private async createEntry(
    group: EConfiguratorFileGroup,
    entryType: string,
    files: File[],
    uploads: IConfiguratorUpload[]
  ): Promise<number | null> {
    const packet = (await this.track(this.packetService.createAttachmentPacket(files), uploads)) as {
      id: number;
    };
    const entry: Entry = await lastValueFrom(
      this.entryApi.addEntry({
        id: this.projectId,
        type: entryType,
        name: this.translate.instant(`${T}.entryName`, {
          group: this.translate.instant(CONFIGURATOR_FILE_GROUPS[group].title)
        }),
        description: '',
        objectType: 'data-box-2',
        attachmentPacketId: packet.id
      })
    );

    this.store.setAnswer(`fileEntries.${entryType}`, entry.id);
    this.updateFiles(entryType, () =>
      (entry.attachments || []).map((attachment) => new Attachment(attachment))
    );
    return entry.id;
  }

  /** Follows the progress of an upload request and resolves with its result. */
  private track(request: Observable<UploadEvent>, uploads: IConfiguratorUpload[]): Promise<unknown> {
    return new Promise((resolve, reject) => {
      let result: unknown;
      request.subscribe({
        next: (event) => {
          this.setProgress(uploads, event.progress);
          if (event.result) result = event.result;
        },
        error: reject,
        complete: () => resolve(result)
      });
    });
  }

  private startUpload(group: EConfiguratorFileGroup, file: File): IConfiguratorUpload {
    const upload: IConfiguratorUpload = {
      id: `upload-${++this.uploadCounter}`,
      group,
      name: file.name,
      progress: 0,
      preview: isImageFile(file) ? URL.createObjectURL(file) : null
    };
    this.uploads.update((list) => [...list, upload]);
    return upload;
  }

  private setProgress(uploads: IConfiguratorUpload[], progress: number): void {
    const ids = uploads.map((upload) => upload.id);
    this.uploads.update((list) =>
      list.map((upload) => (ids.includes(upload.id) ? { ...upload, progress } : upload))
    );
  }

  private finishUpload(upload: IConfiguratorUpload): void {
    if (upload.preview) URL.revokeObjectURL(upload.preview);
    this.uploads.update((list) => list.filter((item) => item.id !== upload.id));
  }

  private updateFiles(entryType: string, update: (list: Attachment[]) => Attachment[]): void {
    this.files.update((files) => ({ ...files, [entryType]: update(files[entryType] || []) }));
    this.syncCounts();
  }

  /** Counts per group in the answers, for the summary and the review. */
  private syncCounts(): void {
    Object.values(EConfiguratorFileGroup).forEach((group) => {
      const count = this.filesOf(group).length;
      if ((this.store.answers().fileCounts?.[group] || 0) !== count) {
        this.store.setAnswer(`fileCounts.${group}`, count || null);
      }
    });
  }
}
